import {
  LinearFilter,
  ClampToEdgeWrapping,
  Color,
  DoubleSide,
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  Scene,
  Vector2,
  Vector3,
  WebGLRenderer,
  MeshBasicMaterial,
  Camera,
  Object3D,
  ShaderMaterial,
  Box3,
  Clock,
} from 'three';
import BaseScene from './base-scene';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import RenderController from './renderer-controller';
import { EnterAnimContoller } from './enter-anim-controller';
import CamController from './cam-controller';
// import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { Composer } from './composer';
import { FloatController } from './float-controller';
import throughVert from '../shaders/throughVert';
// import throughFrag from '../shaders/throughFrag';
import textFrag from '../shaders/textFrag';
import cloudFrag from '../shaders/cloudFrag';
import texManager from './tex-manager';
import { addPSSource, animPS, resetPS } from './particles-controller';
import emitter, {
  EVENT_ENTER_ANIM_FINISH,
  EVENT_NAME_ONLOAD,
} from '../utils/mitt';
import SposController from './spos-controller';
import { RENDER_ORDER } from '../comon';

export const FPS = 24;

interface Uniforms {
  [key: string]: {
    value: any;
  };
}

export default class XJSJScene extends BaseScene {
  ctr!: RenderController;
  isInteract = false;

  viewHeight = 700;

  audioStartIndex = -1;
  audioEndIndex = 10;

  mouse = new Vector2();
  lastTouch = new Vector2();

  floatController!: FloatController;
  enterAnimController!: EnterAnimContoller;
  camCtr!: CamController;
  maxCameraState = 6;

  composer: Composer;
  cloudUS: Uniforms[] = [];

  clickableObjs: Mesh[] = [];
  sposController!: SposController;

  clock = new Clock();
  hasStarted = false;

  constructor(renderer: WebGLRenderer, ctr: RenderController) {
    super(renderer);

    this.ctr = ctr;

    this.scene = new Scene();
    this.scene.background = new Color(0);
    this.scene.name = 'XJSJScene';

    this.camera = new PerspectiveCamera(
      64,
      this.width / this.height,
      0.1,
      1000
    );
    this.camera.name = 'XJSJCamera';
    this.camera.position.set(0, 0, 50);
    this.camera.lookAt(new Vector3());
    this.scene.add(this.camera);

    this.loadModel();
    this.composer = new Composer(this);
    resetPS(this.scene);

    window.addEventListener('resize', () => {
      this.onWindowResize();
    });

    window.addEventListener('wheel', this.onWheel.bind(this));

    window.addEventListener('click', () => {
      // console.log(this.renderer.info);
    });

    emitter.on(EVENT_NAME_ONLOAD, () => {
      !this.hasStarted && this.start();
    });
  }

  loadModel() {
    const loader = new GLTFLoader();
    const modelId = 'scene.glb';
    this.updateProgress(modelId, 0);
    loader.load(process.env.PUBLIC_URL + `/models/${modelId}`, gltf => {
      this.updateProgress(modelId, 1);
      // console.log(gltf);
      const tagObjs: Object3D[] = [];
      const floatableObjs: Object3D[] = [];
      gltf.scene.traverse(child => {
        if (child.name.includes('float')) {
          floatableObjs.push(child);
        }
        if (child.name.includes('tag')) {
          tagObjs.push(child);
        }
        if (child.name.includes('clickable') && child instanceof Mesh) {
          child.visible = false;
          this.clickableObjs.push(child);
        }
        if (child instanceof Mesh) {
          child.renderOrder = RENDER_ORDER.DEFAULT;
          let mat = child.material as MeshStandardMaterial;
          const map = mat.map!;
          if (map) {
            // map.encoding = LinearEncoding;
            map.wrapS = ClampToEdgeWrapping;
            map.wrapT = ClampToEdgeWrapping;
            map.magFilter = LinearFilter;
            map.minFilter = LinearFilter;
            map.needsUpdate = true;
            // const b = child.geometry.boundingBox;
            // const meshBound = b.max.clone().sub(b.min);
            if (child.name.includes('cloud')) {
              const us = {
                map: { value: map },
                noiseTex: { value: texManager.get('noise') },
                time: { value: 0 },
                opacity: { value: 1 },
                alphaScaleFactor: {
                  value: child.name.includes('enter') ? 2 : 1,
                },
              };
              this.cloudUS.push(us);
              const cloudMat = new ShaderMaterial({
                vertexShader: throughVert,
                fragmentShader: cloudFrag,
                transparent: true,
                side: DoubleSide,
                uniforms: us,
                depthWrite: false,
              });
              child.material = cloudMat;
            } else if (child.name.includes('text')) {
              const us = {
                map: { value: map },
                noiseTex: { value: texManager.get('noise') },
                time: { value: 0 },
                opacity: { value: 1 },
              };
              this.cloudUS.push(us);
              const textMat = new ShaderMaterial({
                vertexShader: throughVert,
                fragmentShader: textFrag,
                transparent: true,
                side: DoubleSide,
                uniforms: us,
              });
              child.material = textMat;
            } else {
              child.material = new MeshBasicMaterial({
                map,
                transparent: true,
                side: DoubleSide,
                alphaTest: 0.1,
              });
            }
          }
          // cache
          child.geometry && this.geos.push(child.geometry);
          child.material && this.mats.push(child.material);
          mat && this.mats.push(mat);
          mat.map && this.texs.push(mat.map);
          mat.metalnessMap && this.texs.push(mat.metalnessMap);
          mat.roughnessMap && this.texs.push(mat.roughnessMap);
        }
      });
      this.floatController = new FloatController(floatableObjs);
      this.sposController = new SposController(
        tagObjs.concat(this.clickableObjs)
      );

      this.camera = this.findObjectByName<Camera>(
        gltf.cameras,
        'Camera'
      )! as PerspectiveCamera;
      this.composer.setCamera(this.camera);

      // Enter Animation Controller
      this.enterAnimController = new EnterAnimContoller(gltf);

      // Camera Controller
      const camIndices = Array.from(
        { length: this.maxCameraState },
        (_, i) => i
      );
      const cams = camIndices.map(i => {
        const cam = this.findObjectByName<Object3D>(gltf.cameras, `cam-${i}`);
        if (cam) return cam;
        return new Object3D();
      });
      this.camCtr = new CamController(this.camera, cams, this.clickableObjs);

      //add ps
      this.clickableObjs.forEach((obj, i) => {
        const names = obj.name.split('-');
        let sca = Number(names[names.length - 1]);
        sca = isNaN(sca) ? 1 : sca;
        // console.log(obj.name, sca);
        addPSSource(
          {
            pos: obj.position,
            posRandomBox: new Box3(
              new Vector3(-6, -1.5, -1.5).multiplyScalar(sca),
              new Vector3(6, 1.5, 1.5).multiplyScalar(sca)
            ),
            psNum: 22,
            reborn: true,
            maxSize: 2.5 * sca,
            minSize: 1 * sca,
            initVelRandom: 1,
            life: 4,
            lifeRandom: 2,
            autoDispose: false,
            speedFactor: 0.13 * sca,
          },
          false
        );
      });

      this.scene.add(gltf.scene);
      this.onWindowResize();
    });
  }

  async start() {
    this.hasStarted = true;
    await this.enterAnimController.play(() => {
      emitter.emit(EVENT_ENTER_ANIM_FINISH);
    }, 0);
    await this.camCtr.start();
  }
  findObjectByName<T extends { name: string }>(objects: T[], name: string) {
    let resultObj: T | undefined;
    objects.forEach(obj => {
      if (obj.name === name) {
        resultObj = obj;
      }
    });
    return resultObj;
  }
  onWheel(event: WheelEvent) {
    if (event.deltaY > 0 && this.camCtr.isEnabled) {
      this.camCtr.nextState();
    } else if (event.deltaY < 0 && this.camCtr.isEnabled) {
      this.camCtr.prevState();
    }
  }

  onTouchStart(e: TouchEvent) {
    this.isInteract = true;
    const t = e.touches[0];
    const x = this.needRotate ? t.pageY : t.pageX;
    const y = this.needRotate ? this.height - t.pageX : t.pageY;
    this.lastTouch.set(x, y);
  }

  onTouchMove(e: TouchEvent) {
    if (this.isInteract) {
      const t = e.touches[0];
      const x = this.needRotate ? t.pageY : t.pageX;
      const y = this.needRotate ? this.height - t.pageX : t.pageY;

      const pos = new Vector2(x, y);
      this.lastTouch = pos;
    }
  }

  onTouchEnd(e: TouchEvent) {
    this.isInteract = false;
  }
  lastTime = 0;
  anim(time: number, delta: number) {
    const t = this.clock.getElapsedTime();
    const dt = t - this.lastTime;
    this.lastTime = t;

    if (this.camCtr && !this.enterAnimController.isAnimating)
      this.camCtr.update();
    if (this.floatController) {
      this.floatController.anim(t, dt);
    }
    if (this.sposController) {
      this.sposController.update(this.camera as PerspectiveCamera);
    }
    this.cloudUS.forEach(us => {
      us.time.value = t;
    });
    animPS(t, dt, this.camera);
    this.render(t, dt);
  }

  render(time: number, delta: number) {
    if (this.composer) {
      this.composer.render();
    }
  }

  onWindowResize() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    const cam = this.camera as PerspectiveCamera;
    cam.aspect = window.innerWidth / window.innerHeight;
    cam.updateProjectionMatrix();
    this.composer.setSize(window.innerWidth, window.innerHeight);
    super.onWindowResize();
  }
  dispose() {
    super.dispose();
  }
}
